import React, { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

/**
 * Install prompt banner
 * - Android/desktop Chromium: uses beforeinstallprompt
 * - iOS Safari: shows manual "Add to Home Screen" hint
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isIos, setIsIos] = useState(false);

  useEffect(() => {
    const dismissed = localStorage.getItem("ly_install_dismissed");
    const ua = window.navigator.userAgent || "";
    const iosDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;

    if (isStandalone || dismissed === "1") return;

    if (iosDevice) {
      setIsIos(true);
      setVisible(true);
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const installed = () => {
      setVisible(false);
      localStorage.setItem("ly_install_dismissed", "1");
    };
    window.addEventListener("appinstalled", installed);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installed);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      localStorage.setItem("ly_install_dismissed", "1");
    }
    setDeferred(null);
    setVisible(false);
  };

  const dismiss = () => {
    localStorage.setItem("ly_install_dismissed", "1");
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="install-prompt"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:w-[380px] z-50 border border-yellow-500/40 bg-zinc-950 shadow-2xl"
    >
      <div className="absolute left-0 top-0 w-[3px] h-full bg-yellow-500" />
      <div className="p-4 flex items-start gap-3">
        <div className="w-9 h-9 bg-yellow-500 flex items-center justify-center flex-shrink-0">
          <DownloadSimple size={20} weight="bold" className="text-zinc-950" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="overline text-yellow-500 mb-1">Install</div>
          <div className="font-display font-bold text-zinc-100 text-base leading-tight mb-1">
            Get LOAD/YARD as an app
          </div>
          {isIos ? (
            <p className="text-zinc-400 text-xs leading-relaxed">
              Tap <span className="text-zinc-200 font-semibold">Share</span>, then{" "}
              <span className="text-zinc-200 font-semibold">Add to Home Screen</span> to install.
            </p>
          ) : (
            <p className="text-zinc-400 text-xs leading-relaxed">
              Install it for one-tap access, offline shell and a full-screen experience.
            </p>
          )}
          {!isIos && (
            <Button
              data-testid="install-btn"
              onClick={install}
              className="mt-3 rounded-sm bg-yellow-500 hover:bg-yellow-400 text-zinc-950 font-semibold tracking-wide h-9 px-4 text-xs"
            >
              Install App
            </Button>
          )}
        </div>
        <button
          data-testid="install-dismiss"
          onClick={dismiss}
          className="p-1 text-zinc-500 hover:text-zinc-200 transition-colors"
          aria-label="Dismiss"
        >
          <X size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}
